import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import ElipsisCell from '../../../../components/TableBox/Cells/EllipsisCell';
import ElipsisText from '../../../../components/ElipsisText';
import Link from '../../../../components/Link';
import Badge from '../../../../components/Badge';
import Button from '../../../../components/Button';
import { TableContext } from '../../../../contexts/TableContext';
import { AuthContext } from '../../../../contexts/AuthContext';
import { RowClassName, Modules, RouteParamsTypes, RoleCodes } from '../../../../enums';
import { VALIDATION_FIELD_MAPPING, PROPERTY_VISIT_STATUS } from '../../../../utils/appConstant';
import { PropertyVisitListItem } from '../../../../mappers/property-visit.mapper';

interface RowProps {
  item: PropertyVisitListItem;
  className?: RowClassName;
  handleRemoveClick: (id: string) => void;
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case PROPERTY_VISIT_STATUS.toBeReviewed: return 'secondary';
    case PROPERTY_VISIT_STATUS.needsClarification: return 'warning';
    case PROPERTY_VISIT_STATUS.completed: return 'success';
    case PROPERTY_VISIT_STATUS.notQualified: return 'danger';
    default: return 'secondary';
  }
};

const EDITABLE_STATUSES = [PROPERTY_VISIT_STATUS.toBeReviewed, PROPERTY_VISIT_STATUS.needsClarification];

const Row: React.FC<RowProps> = ({ item, className, handleRemoveClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { isSystemAdmin, loggedInUser } = useContext(AuthContext);

  const isAdmin = isSystemAdmin === 'true' || loggedInUser?.role_code === RoleCodes.admin;
  const canEdit = isAdmin || EDITABLE_STATUSES.includes(item.visit_status);

  const handleEditViewClick = () => {
    const route = canEdit ? RouteParamsTypes.edit : RouteParamsTypes.view;
    navigate(`${Modules.propertyVisits}${route}/${item.id}`);
  };

  const handleTasksClick = () => {
    const route = canEdit ? RouteParamsTypes.edit : RouteParamsTypes.view;
    navigate(`${Modules.propertyVisits}${route}/${item.id}?tab=tasks`);
  };

  const handleDocumentsClick = () => {
    const route = canEdit ? RouteParamsTypes.edit : RouteParamsTypes.view;
    navigate(`${Modules.propertyVisits}${route}/${item.id}?tab=documents`);
  };

  const propertyNode = (
    <div className="d-flex flex-column">
      <span className="fw-semibold">{item.property_name}</span>
      <small className="text-muted">{item.property_city}</small>
    </div>
  );

  const visitedByNode = (
    <div className="d-flex flex-column">
      <span>{item.visited_by_name}</span>
      <small className="text-muted">{item.visited_by_role_label}</small>
    </div>
  );

  const actionsNode = (
    <div className="d-flex align-items-center">
      {/* Edit or View based on role + status */}
      <Button
        className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
        icon={canEdit
          ? <i className="ri-edit-box-line ri-20px"></i>
          : <i className="ri-eye-line ri-20px"></i>}
        onClick={handleEditViewClick}
      />

      {/* Tasks — redirect to tasks tab */}
      <Tooltip title={t('propertyVisits.list.pageTexts.table.tasksTooltip')} arrow classes={{ tooltip: 'tooltip' }}>
        <span>
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-task-line ri-20px"></i>}
            onClick={handleTasksClick}
          />
        </span>
      </Tooltip>

      {/* Documents — redirect to documents tab */}
      <Tooltip title={t('propertyVisits.list.pageTexts.table.documentsTooltip')} arrow classes={{ tooltip: 'tooltip' }}>
        <span>
          <Button
            className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
            icon={<i className="ri-file-list-line ri-20px"></i>}
            onClick={handleDocumentsClick}
          />
        </span>
      </Tooltip>

      {/* Last updated */}
      <Tooltip
        title={
          <>
            <div>{t('propertyVisits.list.pageTexts.table.lastUpdatedBy')} {item.updated_by_name}</div>
            <div>{t('propertyVisits.list.pageTexts.table.lastUpdatedOn')} {item.updated_at}</div>
          </>
        }
        arrow
        classes={{ tooltip: 'tooltip' }}
      >
        <i className="ri-timer-line ri-20px cursor-pointer me-2 waves-effect waves-light rounded-pill"></i>
      </Tooltip>

      {/* Delete — only when status is To be Reviewed */}
      {item.visit_status === PROPERTY_VISIT_STATUS.toBeReviewed && (
        <Button
          className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
          icon={<i className="ri-delete-bin-line ri-20px"></i>}
          onClick={() => handleRemoveClick(item.id)}
        />
      )}
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.visitDate) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <Link
                  text={item.visit_date ?? '-'}
                  href={`${Modules.propertyVisits}${canEdit ? RouteParamsTypes.edit : RouteParamsTypes.view}/${item.id}`}
                  onClick={handleEditViewClick}
                />
              }
            />
          );
        }
        if (headCell.id === VALIDATION_FIELD_MAPPING.propertyName) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={propertyNode} />;
        }
        if (headCell.id === VALIDATION_FIELD_MAPPING.visitType) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.visit_type_label ?? '-'} />;
        }
        if (headCell.id === VALIDATION_FIELD_MAPPING.visitedBy) {
          return <ElipsisCell key={headCell.id} width={headCell.width} node={visitedByNode} />;
        }
        if (headCell.id === VALIDATION_FIELD_MAPPING.visitNote) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={<ElipsisText text={item.visit_note ?? '-'} width="100%" />}
            />
          );
        }
        if (headCell.id === VALIDATION_FIELD_MAPPING.visitStatus) {
          return (
            <td key={headCell.id}>
              <Badge
                text={item.visit_status_label ?? '-'}
                variant={getStatusVariant(item.visit_status)}
              />
            </td>
          );
        }
        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return <td key={headCell.id} width={headCell.width}>{actionsNode}</td>;
        }
        return (
          <ElipsisCell key={headCell.id} width={headCell.width} text={(item as any)[headCell.id] ?? '-'} />
        );
      })}
    </tr>
  );
};

export default Row;
